import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { UseCases } from "@planzone/core";
import { useRetrieveProjects } from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import { ProjectSection } from "@common/projectSection";
import { ListToggleArrow } from "@common/listToggleArrow";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { Flag } from "@common/infiniteScroll";
import { PenIcon } from "@common/penIcon";
import { TrashIcon } from "@common/trashIcon";
import { VisibilitySwitch } from "@common/visibilitySwitch";
import { ProjectPortfolioIcon } from "@common/projectPortfolioIcon";
import { Props } from "./types";
import { Loader } from "./loader";
import styles from "./styles.scss";
import { ScrollingText } from "@common/scrollingText";

function isColorLight(color: string) {
  const hex = color.replace("#", "");
  const c_r = parseInt(hex.substring(0, 2), 16);
  const c_g = parseInt(hex.substring(2, 2 + 2), 16);
  const c_b = parseInt(hex.substring(4, 4 + 2), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;

  return brightness > 155;
}

function ProjectPortfolio({
  projectPortfolio,
  filter,
  columnSizes,
  resizeCol,
  resizeDone,
}: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const push = useNavigate();
  const {
    isRequestPending,
    projects,
    index,
    canRetrieveNextProjects,
    retrieveProjects,
  } = useRetrieveProjects(
    {
      ...filter,
      projectPortfolios: [projectPortfolio],
      projects: projectPortfolio.projects,
    },
    "ascendingName",
    50,
  );
  const get = () =>
    canRetrieveNextProjects &&
    projectPortfolio.projects.length > 0 &&
    !isRequestPending &&
    retrieveProjects({
      filter: {
        ...filter,
        projectPortfolios: [projectPortfolio],
        projects: projectPortfolio.projects,
      },
      sort: "ascendingName",
      index,
      size: 50,
    });

  return (
    <VisibilitySwitch
      defaultState={true}
      renderChildren={(isVisible, show, hide) => (
        <>
          <ProjectSection
            header={(filtered) => (
              <div
                className={classNames(styles.portfolioInfo, {
                  [styles.dark]: isColorLight(projectPortfolio.color),
                })}
                style={{ backgroundColor: projectPortfolio.color }}
              >
                <div
                  className={styles.iconBox}
                  onClick={!isVisible ? show : hide}
                >
                  <ListToggleArrow isOpen={isVisible} />
                </div>
                <div className={styles.row} onClick={!isVisible ? show : hide}>
                  <ProjectPortfolioIcon className={styles.portfolioIcon} />
                  <h2 className={styles.name}>
                    {projectPortfolio.name} ({filtered.length})
                  </h2>
                </div>
                {isVisible && (
                  <div className={styles.contextualMenu}>
                    <SecureContextualMenu
                      items={[
                        {
                          name: t("projectPortfoliosPage.editProjectPortfolio"),
                          useCase: UseCases.editProjectPortfolio,
                          renderIcon: (className: string) => (
                            <PenIcon
                              className={classNames(className, styles.menuIcon)}
                            />
                          ),
                          act: () =>
                            pushView({
                              key: UseCases.editProjectPortfolio,
                              data: projectPortfolio,
                            }),
                        },
                        {
                          name: t(
                            "projectPortfoliosPage.removeProjectPortfolio",
                          ),
                          useCase: UseCases.removeProjectPortfolio,
                          color: "#DB5F63FF",
                          renderIcon: (className: string) => (
                            <TrashIcon
                              className={classNames(
                                className,
                                styles.removeIcon,
                              )}
                            />
                          ),
                          act: () =>
                            pushView({
                              key: UseCases.removeProjectPortfolio,
                              data: projectPortfolio,
                            }),
                        },
                      ]}
                    />
                  </div>
                )}
              </div>
            )}
            columnSizes={columnSizes}
            projects={projects.filter(({ isTemplate }) => !isTemplate)}
            loader={isRequestPending && <Loader />}
            filter={{ ...filter, projectPortfolios: [projectPortfolio] }}
            isOpen={isVisible}
            resizeCol={resizeCol}
            resizeDone={resizeDone}
          />
          <Flag
            name={projectPortfolio.uuid}
            retrieveItems={get}
            isDisabled={
              !canRetrieveNextProjects ||
              isRequestPending ||
              projectPortfolio.projects.length === 0
            }
          />
        </>
      )}
    />
  );
}

export { ProjectPortfolio };
